import {
  Navigate,
  useLocation
} from 'react-router-dom'
import { useAuthChange, AuthChangeEvent, useAuthStatus } from './hooks'
import { Flows } from '../lib/allauth'
import { safeReturnPath } from '../lib/sso'

export const URLs = Object.freeze({
  LOGIN_URL: '/account/login',
  LOGIN_REDIRECT_URL: '/',
  LOGOUT_REDIRECT_URL: '/account/login?logged_out=1'
})

const flow2path = {}
flow2path[Flows.LOGIN] = '/account/login'

export function pathForFlow (flow, typ) {
  let key = flow.id
  if (typeof flow.types !== 'undefined') {
    typ = typ ?? flow.types[0]
    key = `${key}:${typ}`
  }
  const path = flow2path[key] ?? flow2path[flow.id]
  if (!path) {
    throw new Error(`Sign-in flow "${flow.id}" is disabled. Use Microsoft SSO. Contact IT if you cannot sign in.`)
  }
  return path
}

export function pathForPendingFlow (auth) {
  const flow = auth.data.flows.find(flow => flow.is_pending)
  if (flow) {
    return pathForFlow(flow)
  }
  return null
}

function navigateToPendingFlow (auth) {
  const path = pathForPendingFlow(auth)
  if (path) {
    return <Navigate to={path} />
  }
  return null
}

export function AuthenticatedRoute ({ children }) {
  const location = useLocation()
  const [, status] = useAuthStatus()
  const next = `next=${encodeURIComponent(location.pathname + location.search + location.hash)}`
  if (status.isAuthenticated) {
    return children
  } else {
    return <Navigate to={`${URLs.LOGIN_URL}?${next}`} />
  }
}

export function AnonymousRoute ({ children }) {
  const [, status] = useAuthStatus()
  const location = useLocation()
  if (!status.isAuthenticated) {
    return children
  } else {
    return <Navigate to={safeReturnPath(new URLSearchParams(location.search).get('next'))} replace />
  }
}

export function AuthChangeRedirector ({ children }) {
  const [auth, event] = useAuthChange()
  const location = useLocation()
  const next = safeReturnPath(new URLSearchParams(location.search).get('next'))
  switch (event) {
    case AuthChangeEvent.LOGGED_OUT:
      return <Navigate to={URLs.LOGOUT_REDIRECT_URL} />
    case AuthChangeEvent.LOGGED_IN:
      return <Navigate to={next} />
    case AuthChangeEvent.REAUTHENTICATED:
      return <Navigate to={next} />
    case AuthChangeEvent.REAUTHENTICATION_REQUIRED: {
      const path = pathForFlow(auth.data.flows[0])
      const reauthNext = `next=${encodeURIComponent(location.pathname + location.search)}`
      return <Navigate to={`${path}?${reauthNext}`} state={{ reauth: auth }} />
    }
    case AuthChangeEvent.FLOW_UPDATED: {
      const pendingFlow = navigateToPendingFlow(auth)
      if (!pendingFlow) {
        throw new Error()
      }
      return pendingFlow
    }
    default:
      break
  }
  // ...stay where we are
  return children
}
